/* eslint-disable camelcase */
// import sensors from 'sa-sdk-javascript'
import { SensorsType } from '@/constants/sensorsBurying'
import { formatTime } from '@/utils/common-js/date'
import { useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAdChannelStore } from '@/store/adChannelCode.store'
import { useUserStore } from '@/store/user.store'
import { ThirdpartyType } from '@/constants/product-enum'
import { getAdChannelCodeByRouteCodeRes, RqResult } from '@/apis'
import { ENV } from '@/env'
import { checkDeviceBrand } from '@/utils/device'
import { YesNo } from '@/constants/user'
import { browserType } from '@/utils/common-js'

interface CommonSensorsParamsType {
  ad_channel_code:string
  user_behavior_id: string
  is_source: string,
  brand: string,
}

interface getUserMatchedProductListRes {
  productId: number | null,
  productList:Array<string | null | undefined>
  selectProductList:Array<string | null | undefined>
}

interface BackendProductTabOpenParams {
  pageName: '授权页' | '结果页'
  productId: number | null
  productName: string
  opened: boolean
}

/** 路由码转换结果，中文枚举值直接用于神策字段 route_code_convert_result */
export enum RouteCodeConvertResultText {
  Success = '转换成功',
  Offline = '路由码失效',
  Error = '接口异常'
}

/** 路由码转换后的处理分支，中文枚举值直接用于神策字段 target_type */
export enum RouteCodeTargetTypeText {
  Normal = '普通流程',
  UnionLogin = '联登流程',
  CrmJump = 'CRM企微跳转',
  ExternalUrl = '外部链接跳转',
  Invalid = '失效页'
}

/** 链接失效原因，中文枚举值直接用于神策字段 invalid_link_reason */
export enum InvalidLinkReasonText {
  RouteCodeOffline = '路由码已失效',
  ChannelDisabled = '渠道已禁用',
  Unknown = '未知原因'
}

export interface InvalidLinkContext {
  /** 用户打开页面时的完整原始 URL */
  original_url: string
  /** 用户跳转到 /failure 前的完整 URL */
  before_failure_url: string
  /** 链接失效原因，中文枚举值 */
  invalid_link_reason: InvalidLinkReasonText
  /** URL 参数中的原始路由码 adRouteCode */
  ad_route_code: string
  /** 当前渠道码或路由码转换出的渠道码 */
  ad_channel_code: string
}

export interface RouteCodeConvertParams {
  /** 用户打开页面时的完整原始 URL */
  original_url: string
  /** URL 参数中的原始路由码 adRouteCode */
  ad_route_code: string
  /** getAdChannelCodeByRouteCode 接口完整返回数据，JSON 字符串 */
  route_code_api_response: string
  /** 路由码转换结果，中文枚举值 */
  route_code_convert_result: RouteCodeConvertResultText
  /** 路由码转换出的目标渠道码 */
  target_ad_channel_code: string
  /** 转换后的前端处理分支，中文枚举值 */
  target_type: RouteCodeTargetTypeText
  /** 接口返回的 flgOnline，表示路由码是否在线 */
  is_online: YesNo | ''
  /** 接口返回的 flgUnion，表示是否开启联登 */
  is_union: YesNo | ''
  /** 接口返回的导流产品 ID */
  product_id: number | ''
  /** 接口返回的导流产品名称 */
  product_name: string
  /** 接口返回的 CRM 企微跳转地址 */
  crm_jump_url: string
  /** 接口返回的产品计划目标地址 */
  plan_target_url: string
  /** 前端准备跳转、替换或外跳的完整目标 URL */
  final_url: string
  /** 接口异常时的错误信息 */
  error_message: string
}

export interface ClickByPeriodParams {
  /** 路由码 */
  adRouteCode: string
  /** 渠道码 */
  adChannelCode: string
  /** 导流产品名称 */
  productName: string
  /** 导流产品跳转地址 */
  productUrl: string
}

interface BuildRouteCodeConvertParamsOptions {
  originalUrl: string
  adRouteCode: string
  finalUrl: string
  response?: getAdChannelCodeByRouteCodeRes
  error?: unknown
}

const INVALID_LINK_CONTEXT_KEY = 'invalid_link_sensors_context'
const ENTRY_ORIGINAL_URL_KEY = 'entry_original_url_sensors_context'

export const getRouteCodeTargetInfo = (response: getAdChannelCodeByRouteCodeRes) => {
  if (!response.flgOnline) {
    return {
      result: RouteCodeConvertResultText.Offline,
      targetType: RouteCodeTargetTypeText.Invalid
    }
  }

  if (response.planTargetUrl) {
    return {
      result: RouteCodeConvertResultText.Success,
      targetType: RouteCodeTargetTypeText.ExternalUrl
    }
  }

  if (response.crmJumpUrl) {
    return {
      result: RouteCodeConvertResultText.Success,
      targetType: RouteCodeTargetTypeText.CrmJump
    }
  }

  if (response.flgUnion === YesNo.Yes && response.productId) {
    return {
      result: RouteCodeConvertResultText.Success,
      targetType: RouteCodeTargetTypeText.UnionLogin
    }
  }

  return {
    result: RouteCodeConvertResultText.Success,
    targetType: RouteCodeTargetTypeText.Normal
  }
}

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  return ''
}

export const buildRouteCodeConvertParams = ({
  originalUrl,
  adRouteCode,
  response,
  finalUrl,
  error
}: BuildRouteCodeConvertParamsOptions): RouteCodeConvertParams => {
  if (!response) {
    return {
      original_url: originalUrl,
      ad_route_code: adRouteCode,
      route_code_api_response: '',
      route_code_convert_result: RouteCodeConvertResultText.Error,
      target_ad_channel_code: '',
      target_type: RouteCodeTargetTypeText.Invalid,
      is_online: '',
      is_union: '',
      product_id: '',
      product_name: '',
      crm_jump_url: '',
      plan_target_url: '',
      final_url: finalUrl,
      error_message: getErrorMessage(error)
    }
  }

  const targetInfo = getRouteCodeTargetInfo(response)

  return {
    original_url: originalUrl,
    ad_route_code: adRouteCode,
    route_code_api_response: JSON.stringify(response),
    route_code_convert_result: targetInfo.result,
    target_ad_channel_code: response.adChannelCode || '',
    target_type: targetInfo.targetType,
    is_online: response.flgOnline,
    is_union: response.flgUnion,
    product_id: response.productId || '',
    product_name: response.productName || '',
    crm_jump_url: response.crmJumpUrl || '',
    plan_target_url: response.planTargetUrl || '',
    final_url: finalUrl,
    error_message: ''
  }
}

export const buildFailureUrl = (url: string) => {
  try {
    return `${new URL(url).origin}/failure`
  } catch (error) {
    return '/failure'
  }
}

export const initEntryOriginalUrl = (url = window.location.href) => {
  sessionStorage.setItem(ENTRY_ORIGINAL_URL_KEY, url)
}

const getEntryOriginalUrl = (fallbackUrl: string) => sessionStorage.getItem(ENTRY_ORIGINAL_URL_KEY) || fallbackUrl

const getUrlSearchParam = (url: string, key: string) => {
  try {
    return new URL(url).searchParams.get(key) || ''
  } catch (error) {
    return ''
  }
}

export const saveInvalidLinkContext = (context: InvalidLinkContext) => {
  const entryOriginalUrl = getEntryOriginalUrl(context.original_url)
  const entryRouteCode = getUrlSearchParam(entryOriginalUrl, 'adRouteCode')
  const storedContext = sessionStorage.getItem(INVALID_LINK_CONTEXT_KEY)
  if (!storedContext) {
    sessionStorage.setItem(INVALID_LINK_CONTEXT_KEY, JSON.stringify({
      ...context,
      original_url: entryOriginalUrl,
      ad_route_code: context.ad_route_code || entryRouteCode
    }))
    return
  }

  try {
    const previousContext = JSON.parse(storedContext) as InvalidLinkContext
    sessionStorage.setItem(INVALID_LINK_CONTEXT_KEY, JSON.stringify({
      ...context,
      original_url: previousContext.original_url || entryOriginalUrl,
      ad_route_code: previousContext.ad_route_code || context.ad_route_code || entryRouteCode,
      ad_channel_code: context.ad_channel_code || previousContext.ad_channel_code
    }))
  } catch (error) {
    sessionStorage.setItem(INVALID_LINK_CONTEXT_KEY, JSON.stringify({
      ...context,
      original_url: entryOriginalUrl,
      ad_route_code: context.ad_route_code || entryRouteCode
    }))
  }
}

export const getInvalidLinkContext = (fallbackUrl: string): InvalidLinkContext => {
  const context = sessionStorage.getItem(INVALID_LINK_CONTEXT_KEY)
  if (!context) {
    return {
      original_url: fallbackUrl,
      before_failure_url: '',
      invalid_link_reason: InvalidLinkReasonText.Unknown,
      ad_route_code: '',
      ad_channel_code: ''
    }
  }

  try {
    return JSON.parse(context) as InvalidLinkContext
  } catch (error) {
    return {
      original_url: fallbackUrl,
      before_failure_url: '',
      invalid_link_reason: InvalidLinkReasonText.Unknown,
      ad_route_code: '',
      ad_channel_code: ''
    }
  }
}

export const clearInvalidLinkContext = () => {
  sessionStorage.removeItem(INVALID_LINK_CONTEXT_KEY)
  sessionStorage.removeItem(ENTRY_ORIGINAL_URL_KEY)
}

/** 神策埋点-上报地址 */
// eslint-disable-next-line no-shadow
// enum sensorsServerUrl {
//   dev = 'https://qa.insight.yuanxinwallet.com/track/sa.gif?project=yx_dev&token=c9239cb3139077ca',
//   prod = 'https://insight.chongdong.cc/track/sa.gif?project=yx&token=c9239cb3139077ca'
// }

// 用户行为编号相关常量
/**  用户行为编号 */
const USER_BEHAVIOR_ID_KEY = 'user_behavior_code' //
/** 用户行为编号生成时间戳 */
const USER_BEHAVIOR_TIMESTAMP_KEY = 'user_behavior_timestamp'//
/** 过期时间 - 1小时 */
const BEHAVIOR_ID_EXPIRE_TIME = 60 * 60 * 1000 //

type sensorsModule = typeof import('sa-sdk-javascript')
export const initSensorsInstance = (() => {
  let sensorsTaskQueue: Array<(sdk: sensorsModule['default']) => void> = []
  let sensorsInstance: sensorsModule['default'] | null = null
  return async (task: (sdk: sensorsModule['default']) => void) => {
    if (sensorsInstance) {
      task(sensorsInstance)
      return
    }

    sensorsTaskQueue.push(task)
    import('sa-sdk-javascript').then(sensors => {
      sensorsInstance = sensors.default
      sensorsTaskQueue.forEach(task => task(sensorsInstance as sensorsModule['default']))
      sensorsTaskQueue = []
    })
  }
})()

let sensorsInitializedInRuntime = false

export const useSensors = () => {
  const server_url = ENV.VITE_SENSORS_SERVER_URL
  const adChannelCode = useAdChannelStore(state => state.adChannelCode)
  const codeDetailCache = useAdChannelStore(state => state.codeDetailCache)
  const getCodeDetails = useAdChannelStore(state => state.getCodeDetails)
  // 用户信息
  const userInfo = useUserStore(state => state.userInfo)
  const executed = sessionStorage.getItem('hasExecuted')
  const hasExecuted = useRef<string[]>(executed ? JSON.parse(executed) : [])
  const [search] = useSearchParams()

  const syncHasExecuted = () => {
    let execute = sessionStorage.getItem('hasExecuted')
    hasExecuted.current = execute ? JSON.parse(execute) : []
  }

  /**
   * 生成用户行为编号
   * @returns {string}
   * 规则：年/月/日（8位数）+时间（6位数）+ 4位随机数字
   * 例如：20241212143025 + 1234 = 202412121430251234
   */
  const generateUserBehaviorId = (): string => {
    let dateStr = formatTime(new Date(), 'YYYY-MM-DD-hh-mm-ss').split('-')
      .join('')

    // 4位随机数字
    const randomNum = Math.floor(Math.random() * 10000).toString()
      .padStart(4, '0')

    return dateStr + randomNum
  }

  /**
   * @returns {string}
   * 获取或生成用户行为编号
   * 检查是否过期，过期则生成新的
   */
  const getUserBehaviorId = (): string => {
    const storedId = localStorage.getItem(USER_BEHAVIOR_ID_KEY)
    const storedTimestamp = localStorage.getItem(USER_BEHAVIOR_TIMESTAMP_KEY)
    const currentTime = Date.now()

    if (hasExecuted.current.includes('init_behavior_code') && storedId) {
      return storedId
    }

    // 检查是否存在且未过期
    if (storedId && storedTimestamp) {
      const timestamp = parseInt(storedTimestamp, 10)
      if (currentTime - timestamp < BEHAVIOR_ID_EXPIRE_TIME) {
        addExecuted(SensorsType.init_behavior_code)
        return storedId
      }
    }

    // 生成新的行为编号
    const newBehaviorId = generateUserBehaviorId()
    localStorage.setItem(USER_BEHAVIOR_ID_KEY, newBehaviorId)
    localStorage.setItem(USER_BEHAVIOR_TIMESTAMP_KEY, currentTime.toString())
    addExecuted(SensorsType.init_behavior_code)
    return newBehaviorId
  }


  /**
   * @param {RqResult} product 匹配到的产品信息
   * @param {number[]} checkedProduct 勾选的产品列表
   * @returns {getUserMatchedProductListRes}
   * 获取公共埋点参数（包含用户行为编号）
   */
  const getUserMatchedProductList = (product:RqResult, checkedProduct?: number[]): getUserMatchedProductListRes => {
    let productList = []
    let selectProductList = []
    let productId = product?.id || null
    if (product?.thirdpartyType === 7) {
      productList.push(`${product.productName}-${product.yqlWholeProcessBumpVO?.partnerProductName}`)
      selectProductList.push(`${product.productName}-${product.yqlWholeProcessBumpVO?.partnerProductName}`)
    } else {
      productList.push(product?.productName)
      selectProductList.push(product?.productName)
    }

    if (product?.mayAuthProductList && product?.mayAuthProductList?.length > 0) {
      product?.mayAuthProductList.forEach((element:any) => {
        if (element.thirdpartyType === 7) {
          productList.push(`${element.productName}-${element.partnerProductName}`)
          if (checkedProduct && checkedProduct.includes(element.id)) {
            selectProductList.push(`${element.productName}-${element.partnerProductName}`)
          }
        } else {
          productList.push(element.productName)
          if (checkedProduct && checkedProduct.includes(element.id)) {
            selectProductList.push(element.productName)
          }
        }
      })
    }
    return {
      productId,
      productList,
      selectProductList
    }
  }

  const addExecuted = (event: string) => {
    if (!hasExecuted.current.includes(event)) {
      hasExecuted.current.push(event)
      sessionStorage.setItem('hasExecuted', JSON.stringify(hasExecuted.current))
    }
  }

  /**
   * @returns {CommonSensorsParamsType}
   * 获取公共埋点参数（包含用户行为编号）
   */
  const getCommonSensorsParams = () => {
    return {
      ad_channel_code: adChannelCode || search.get('adChannelCode') || localStorage.getItem('ad_channel_code') || '',
      user_behavior_id: getUserBehaviorId(),
      is_source: 'H5',
      brand: checkDeviceBrand(),
    }
  }

  /**
   * 神策埋点-初始化
  */
  const initSensors = () => {
    if (sensorsInitializedInRuntime) {
      return
    }
    sensorsInitializedInRuntime = true
    initSensorsInstance(sensors => sensors.init({
      server_url,
      show_log: false,
      send_type: 'image',
    }))
  }

  /**
   * 神策埋点-登录
   * @param {string} uid 用户uid
  */
  const loginSensors = (uid: string) => {
    initSensorsInstance(sensors => sensors.login(uid))
  }

  const visitMiniProgramPage = (channelCode: string) => {
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_mini_program_page, {
      ...getCommonSensorsParams()
    }))
  }

  const clickOpenMiniProgram = (channelCode: string) => {
    initSensorsInstance(sensors => sensors.track(SensorsType.click_open_mini_program, {
      ...getCommonSensorsParams()
    }))
  }

  /**
   * 神策埋点-访问落地页
  */
  const visitLoadingPageSensors = (code?:string) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_landing_page)) {
      return
    }
    let reportParams = {
      ...getCommonSensorsParams()
    }
    if (code) {
      reportParams.ad_channel_code = code
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_landing_page, reportParams))
    addExecuted(SensorsType.visit_landing_page)
  }

  /**
   * @param {number} phone 手机号
   * 神策埋点-用户点击获取验证码 - 可以进行重复上报
  */
  const userClickGetVerificationCode = (phone:number | string) => {
    initSensorsInstance(sensors => sensors.track(SensorsType.cilck_get_verification_code, {
      ...getCommonSensorsParams(),
      mobile: +phone
    }))
  }

  /**
   * 神策埋点-用户点击登录
   * @param {number} phone 手机号
  */
  const userClickLoginSensors = (phone: number) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.click_login)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.click_login, {
      ...getCommonSensorsParams(),
      mobile: phone
    }))
    addExecuted(SensorsType.click_login)
  }

  /**
   * 神策埋点-用户访问表单页面
   * @param {boolean} federated 是否联登
  */
  const userVisitFormPageSensors = (federated:boolean) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_form_page)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_form_page, {
      ...getCommonSensorsParams(),
      mobile: userInfo?.mobile || localStorage.getItem('user_phone') || '',
      h5_federated_login: federated,
    }))
    addExecuted(SensorsType.visit_form_page)
  }

  /**
   * @param {number} start 开始时间
   * 神策埋点-用户点击提交表单
  */
  const userClickFormInfoSensors = (start:number) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.click_submit_form_info)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.click_submit_form_info, {
      ...getCommonSensorsParams(),
      match_display_time: formatTime(start, 'YYYY-MM-DD hh:mm:ss'),
      match_response_time: formatTime(new Date(), 'YYYY-MM-DD hh:mm:ss')
    }))
    addExecuted(SensorsType.click_submit_form_info)
  }

  /**
   * 神策埋点-用户访问授权页面与二次访问授权页
   * @param {string} productList 匹配的产品列表
   * @param {number} productCount 匹配的产品数量
  */
  const userVisitAuthorizetionPageSensors = (
    productList:string, productCount:number,
    type: SensorsType.visit_authorization_page | SensorsType.visit_re_authorization_page,
    thirdpartyType?:ThirdpartyType,
    productId?:number | null
  ) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(type)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(type, {
      ...getCommonSensorsParams(),
      mobile: userInfo?.mobile || localStorage.getItem('user_phone') || '',
      h5_matched_product_list: productList,
      h5_matched_product_count: productCount,
      thirdparty_type_name:thirdpartyType,
      product_id: productId
    }))
    addExecuted(type)
  }

  /**
   * 神策埋点-用户点击授权或二次点击授权
   * 暂时不会匹配多产品-所以选择产品列表可以用匹配的产品来代替
   * @param {string} productList 匹配的产品列表
   * @param {number} productCount 匹配的产品数量
   * @param {string} selectProduct 选择的产品列表
  */
  const userClickAuthorizetionPageSensors = (
    productList: string,
    productCount: number,
    type: SensorsType.click_authorize | SensorsType.click_re_authorize,
    selectProduct?: string,
    thirdpartyType?: ThirdpartyType,
    productId?: number | null
  ) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(type)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(type, {
      ...getCommonSensorsParams(),
      mobile: userInfo?.mobile || localStorage.getItem('user_phone') || '',
      h5_matched_product_list: productList,
      ProductCount: productCount,
      h5_matched_product_selected_list: selectProduct || productList,
      thirdparty_type_name:thirdpartyType
    }))
    addExecuted(type)
  }

  /**
   * 神策埋点-用户点击授权或访问授权页
   * @param {string} type 访问授权页或点击授权
   * @param {string} product 匹配的产品详情
   * @param {number[]} checkedProduct 多产品时勾选的产品
  */
  const sensorsVisitAuthorizePage = (type: SensorsType.visit_authorization_page | SensorsType.click_authorize | SensorsType.visit_re_authorization_page | SensorsType.click_re_authorize, product?:RqResult, checkedProduct?: number[]) => {
    if (product) {
      try {
        let { productId, productList, selectProductList } = getUserMatchedProductList(product, checkedProduct)
        if (type === SensorsType.visit_authorization_page || type === SensorsType.visit_re_authorization_page) {
          userVisitAuthorizetionPageSensors(
            productList.join(';'),
            productList.length,
            type,
            product.thirdpartyType,
            productId
          )
        } else {
          userClickAuthorizetionPageSensors(productList.join(';'), productList.length, type, selectProductList.join(';'), product.thirdpartyType, productId)
        }
      } catch (err) {
        console.log('埋点上报出错>>>>', type, err)
      }
    }
  }

  /**
   * 神策埋点-用户访问结果页
   * @param {boolean} matchResult 匹配结果
   * @param {boolean} monetization 是否有后端变现
  */
  const visitResultPageSensors = (matchResult: boolean, monetization: boolean) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_result_page)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_result_page, {
      ...getCommonSensorsParams(),
      mobile: userInfo?.mobile || localStorage.getItem('user_phone') || '',
      h5_match_result: matchResult,
      result_page_monetization: monetization
    }))
    addExecuted(SensorsType.visit_result_page)
  }

  /**
   * 神策埋点-用户点击下载App
  */
  const installAppSensors = () => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.click_download_app)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.click_download_app, {
      ...getCommonSensorsParams(),
      mobile: userInfo?.mobile || localStorage.getItem('user_phone') || '',
    }))
    addExecuted(SensorsType.click_download_app)
  }

  /**
   * 神策埋点-用户点击后端H5产品
   * @param {string} productName 产品名称
  */
  const clickH5ProductSensors = (productName: string) => {
    initSensorsInstance(sensors => sensors.track(SensorsType.click_backend_h5_product, {
      ...getCommonSensorsParams(),
      mobile: userInfo?.mobile || localStorage.getItem('user_phone') || '',
      clicked_h5_product: productName
    }))
  }

  const trackBackendProductTabOpen = ({
    pageName,
    productId,
    productName,
    opened
  }: BackendProductTabOpenParams) => {
    initSensorsInstance(sensors => sensors.track(SensorsType.open_backend_product_new_tab, {
      ...getCommonSensorsParams(),
      page_name: pageName,
      product_id: productId,
      product_name: productName,
      browser_type: browserType,
      open_result: opened ? '成功' : '失败'
    }))
  }

  /**
   * 神策埋点-访问挽留弹窗
   * @param {RqResult} product 产品名称
   * @param {boolean} authorize 是否授权
  */
  const visitRetentionPopup = (product:RqResult, authorize:boolean) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_retention_popup)) {
      return
    }
    let { productList } = getUserMatchedProductList(product)
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_retention_popup, {
      ...getCommonSensorsParams(),
      mobile: userInfo?.mobile || localStorage.getItem('user_phone') || '',
      h5_matched_product_list: productList.join(';'),
      authorize_by_clicking: authorize
    }))
    addExecuted(SensorsType.visit_retention_popup)
  }

  /**
   * 神策埋点-访问表单流程4表单页挽留弹窗
  */
  const visitRetainFormPage = () => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_retain_form_page)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_retain_form_page, {
      ...getCommonSensorsParams(),
      submit_form_time:formatTime(new Date(), 'YYYY-MM-DD hh:mm:ss')
    }))
    addExecuted(SensorsType.visit_retain_form_page)
  }

  /**
   * 神策埋点-点击表单流程4表单页挽留弹窗提交按钮
  */
  const clickRetainFormPage = () => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.click_retain_form_page)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.click_retain_form_page, {
      ...getCommonSensorsParams(),
      submit_form_time:formatTime(new Date(), 'YYYY-MM-DD hh:mm:ss')
    }))
    addExecuted(SensorsType.click_retain_form_page)
  }

  /** 神策埋点-点击表单流程4最高价产品挽留弹窗的确认离开按钮，可重复上报 */
  const clickFormTopProductRetainLeave = (productName: string) => {
    initSensorsInstance(sensors => sensors.track(SensorsType.click_form_top_product_retain_leave, {
      ...getCommonSensorsParams(),
      productName
    }))
  }

  /** 神策埋点-访问结果页最高价产品挽留弹窗 */
  const visitResultTopProductRetainPopup = (productName: string) => {
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_result_top_product_retain_popup, {
      ...getCommonSensorsParams(),
      productName
    }))
  }

  /** 神策埋点-点击结果页最高价产品挽留弹窗 */
  const clickResultTopProductRetainPopup = (productName: string) => {
    initSensorsInstance(sensors => sensors.track(SensorsType.click_result_top_product_retain_popup, {
      ...getCommonSensorsParams(),
      productName
    }))
  }

  /**
   * 神策埋点-访问表单流程4表单页挽留弹窗
  */
  const visitFormStayPop = () => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_form_stay_pop)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_form_stay_pop, {
      ...getCommonSensorsParams(),
      submit_form_time:formatTime(new Date(), 'YYYY-MM-DD hh:mm:ss')
    }))
    addExecuted(SensorsType.visit_form_stay_pop)
  }

  /**
   * 神策埋点-点击表单流程4表单页挽留弹窗提交按钮
  */
  const clickFormStayPop = () => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.click_form_stay_pop)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.click_form_stay_pop, {
      ...getCommonSensorsParams(),
      submit_form_time:formatTime(new Date(), 'YYYY-MM-DD hh:mm:ss')
    }))
    addExecuted(SensorsType.click_form_stay_pop)
  }

  /**
   * 神策埋点-哈啰跳转企微数据集
   */
  const clickHelloJumpWeChat = (mobile:string) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.click_hello_redirect)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.click_hello_redirect, {
      ...getCommonSensorsParams(),
      mobile,
    }))
    addExecuted(SensorsType.click_hello_redirect)
  }

  /**
   * 神策埋点-打开自动提交表单弹窗
   */
  const visitAutoSubmitForm = () => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_auto_submit_form)) {
      return
    }
    initSensorsInstance(sensors =>  sensors.track(SensorsType.visit_auto_submit_form, {
      ...getCommonSensorsParams(),
    }))
    addExecuted(SensorsType.visit_auto_submit_form)
  }

  /**
   * 神策埋点-表单页-点击自动提交表单弹窗
   * @param {boolean} isAuto 是否自动提交表单 (是/否)
   */
  const clickAutoSubmitForm = (isAuto:boolean) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.click_auto_submit_form)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.click_auto_submit_form, {
      ...getCommonSensorsParams(),
      isAutoSubmit: isAuto ? '是' : '否',
    }))
    addExecuted(SensorsType.click_auto_submit_form)
  }

  /**
   * 神策埋点-授权页-打开引导授权弹窗
   * @param {RqResult} product 产品信息
   */
  const visitGuideAuthorization = (product:RqResult) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_guide_authorization)) {
      return
    }
    let { productList } = getUserMatchedProductList(product)
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_guide_authorization, {
      ...getCommonSensorsParams(),
      productList: productList.join(';'),
    }))
    addExecuted(SensorsType.visit_guide_authorization)
  }

  /**
   * 神策埋点-授权页-点击引导授权弹窗进行授权
   * @param {RqResult} product 产品信息
   */
  const clickGuideAuthorization = (product:RqResult) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.click_guide_authorization)) {
      return
    }
    let { productList } = getUserMatchedProductList(product)
    initSensorsInstance(sensors => sensors.track(SensorsType.click_guide_authorization, {
      ...getCommonSensorsParams(),
      productList: productList.join(';'),
    }))
    addExecuted(SensorsType.click_guide_authorization)
  }

  /**
   * 神策埋点-授权页-点击引导授权弹窗进行授权
   * @param {string} productName 产品名称
   */
  const visitBackendMonetization = (productName:string) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_backend_monetization)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_backend_monetization, {
      ...getCommonSensorsParams(),
      productName,
    }))
    addExecuted(SensorsType.visit_backend_monetization)
  }

  /**
   * 神策埋点-授权页-点击引导授权弹窗进行授权
   * @param {string} productName 产品名称
   */
  const clickBackendMonetization = (productName: string, productId: number) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.click_backend_monetization)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.click_backend_monetization, {
      ...getCommonSensorsParams(),
      productName,
      productId
    }))
    addExecuted(SensorsType.click_backend_monetization)
  }

  const getCurrentChannelName = async () => {
    const currentCode = adChannelCode || search.get('adChannelCode') || localStorage.getItem('ad_channel_code') || ''
    const channelDetail = codeDetailCache?.adChannelCode === currentCode
      ? codeDetailCache
      : await getCodeDetails(currentCode)
    return channelDetail?.channelName || ''
  }

  /** 神策埋点-点击默认表单页返回，可重复上报 */
  const clickFormBackSensors = async () => {
    const channelName = await getCurrentChannelName()
    initSensorsInstance(sensors => sensors.track(SensorsType.click_count_form_back, {
      ...getCommonSensorsParams(),
      channelName
    }))
  }

  /** 神策埋点-访问二次返回引导后端产品弹窗，可重复上报 */
  const visitSecondBackGuidePopup = async (productName: string) => {
    const channelName = await getCurrentChannelName()
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_2nd_back_guide_pop, {
      ...getCommonSensorsParams(),
      channelName,
      productName
    }))
  }

  /** 神策埋点-点击二次返回引导后端产品弹窗，可重复上报 */
  const clickSecondBackGuidePopup = async (productName: string) => {
    const channelName = await getCurrentChannelName()
    initSensorsInstance(sensors => sensors.track(SensorsType.click_count_2nd_back_guide_popup, {
      ...getCommonSensorsParams(),
      channelName,
      productName
    }))
  }

  /**
   * 神策埋点-联登导联登-访问联登引导页
   */
  const visitJointLoginGuide = () => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_joint_login_guide)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_joint_login_guide, {
      ...getCommonSensorsParams(),
      productId:localStorage.getItem('FLG_UNION_ID') || '',
    }))
    addExecuted(SensorsType.visit_joint_login_guide)
  }

  /**
   * 神策埋点-联登导联登-点击马上领取
   */
  const clickToClaimNow = (claimStatus?:boolean) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.click_to_claim_now)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.click_to_claim_now, {
      ...getCommonSensorsParams(),
      productId: localStorage.getItem('FLG_UNION_ID') || '',
      claimStatus
    }))
    addExecuted(SensorsType.click_to_claim_now)
  }

  /**
   * CRM跳企微，跳转表单
   */
  const clickIntermediateForm = () => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.click_intermediate_form)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.click_intermediate_form, {
      ...getCommonSensorsParams(),
    }))
    addExecuted(SensorsType.click_intermediate_form)
  }

  /**
   * 获取放款半流程埋点参数
  */
  const getDisburseAuthSensorsParams = async (product: RqResult) => {
    const channelName = await getCurrentChannelName()

    return {
      ...getCommonSensorsParams(),
      channelName,
      productName: product?.productName,
    }
  }

  /**
   * 低产值流量-访问个人信息页
  */
  const visitPersonalInfoPage = () => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_personal_Info_page)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_personal_Info_page, {
      ...getCommonSensorsParams(),
    }))
    addExecuted(SensorsType.visit_personal_Info_page)
  }

  /**
   * 低产值流量-访问常用联系人页
  */
  const visitCommonContactsPage = () => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_common_contacts_page)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_common_contacts_page, {
      ...getCommonSensorsParams(),
    }))
    addExecuted(SensorsType.visit_common_contacts_page)
  }

  /**
   * 低产值流量-访问实名认证页
  */
  const visitIdCardInfoPage = () => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_idcard_Info_page)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_idcard_Info_page, {
      ...getCommonSensorsParams(),
    }))
    addExecuted(SensorsType.visit_idcard_Info_page)
  }

  /**
   * 低产值流量-访问实名认证挽留弹窗
  */
  const visitIdRetentionPopup = () => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_id_retention_popup)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_id_retention_popup, {
      ...getCommonSensorsParams(),
    }))
    addExecuted(SensorsType.visit_id_retention_popup)
  }

  /**
   * 低产值流量-点击实名认证挽留弹窗领取额度
  */
  const clickIdPopup = () => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.click_id_popup)) {
      return
    }
    initSensorsInstance(sensors => sensors.track(SensorsType.click_id_popup, {
      ...getCommonSensorsParams(),
    }))
    addExecuted(SensorsType.click_id_popup)
  }

  /**
   * 低产值流量-访问放款半流程授权页
  */
  const visitDisburseAuthPage = async (product: RqResult) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.visit_disburse_auth_page)) {
      return
    }
    const reportParams = await getDisburseAuthSensorsParams(product)
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_disburse_auth_page, reportParams))
    addExecuted(SensorsType.visit_disburse_auth_page)
  }

  /**
   * 低产值流量-点击放款半流程授权
  */
  const clickDisburseAuth = async (product: RqResult) => {
    syncHasExecuted()
    if (hasExecuted.current.includes(SensorsType.click_disburse_auth)) {
      return
    }
    const reportParams = await getDisburseAuthSensorsParams(product)
    initSensorsInstance(sensors => sensors.track(SensorsType.click_disburse_auth, reportParams))
    addExecuted(SensorsType.click_disburse_auth)
  }

  /**
   * 还呗访问量不准确问题，使用还呗 uid 进行埋点上报
  */
  const visitHuanbeiRegisterPage = (hbUid:string) => {
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_huanbei_register_page, {
      ...getCommonSensorsParams(),
      uid: hbUid
    }))
  }

  /**
   * 神策埋点-路由码转换
   * 字段说明见 RouteCodeConvertParams：
   * original_url 用户打开的完整原始 URL
   * ad_route_code 原始路由码
   * route_code_api_response 路由码转换接口完整返回 JSON
   * route_code_convert_result 转换结果
   * target_type 转换后的处理分支
   * final_url 前端准备跳转、替换或外跳的完整目标 URL
   */
  const trackRouteCodeConvert = (params: RouteCodeConvertParams) => {
    initSensorsInstance(sensors => sensors.track(SensorsType.route_code_convert, {
      ...getCommonSensorsParams(),
      ...params
    }))
  }

  /**
   * 神策埋点-时间段导流跳转
   */
  const clickByPeriod = (params: ClickByPeriodParams) => {
    initSensorsInstance(sensors => sensors.track(SensorsType.click_by_period, {
      ...getCommonSensorsParams(),
      ...params
    }))
  }

  /**
   * 神策埋点-访问链接失效页
   * 字段说明见 InvalidLinkContext：
   * original_url 用户打开的完整原始 URL
   * before_failure_url 跳转到失效页前的完整 URL
   * invalid_link_reason 链接失效原因
   * ad_route_code URL 参数中的原始路由码
   * ad_channel_code 当前渠道码或路由码转换出的渠道码
   */
  const trackInvalidLinkPage = (params: InvalidLinkContext) => {
    initSensorsInstance(sensors => sensors.track(SensorsType.visit_invalid_link_page, {
      ...getCommonSensorsParams(),
      ...params
    }))
  }

  return {
    initSensorsInstance,
    initSensors,
    loginSensors,
    visitMiniProgramPage,
    clickOpenMiniProgram,
    visitLoadingPageSensors,
    userClickGetVerificationCode,
    userClickLoginSensors,
    userVisitFormPageSensors,
    userClickFormInfoSensors,
    userVisitAuthorizetionPageSensors,
    userClickAuthorizetionPageSensors,
    visitResultPageSensors,
    sensorsVisitAuthorizePage,
    installAppSensors,
    clickH5ProductSensors,
    trackBackendProductTabOpen,
    visitRetentionPopup,
    visitRetainFormPage,
    clickRetainFormPage,
    clickFormTopProductRetainLeave,
    visitResultTopProductRetainPopup,
    clickResultTopProductRetainPopup,
    visitFormStayPop,
    clickFormStayPop,
    clickHelloJumpWeChat,
    visitAutoSubmitForm,
    clickAutoSubmitForm,
    visitGuideAuthorization,
    clickGuideAuthorization,
    visitBackendMonetization,
    clickBackendMonetization,
    clickFormBackSensors,
    visitSecondBackGuidePopup,
    clickSecondBackGuidePopup,
    visitJointLoginGuide,
    clickToClaimNow,
    clickIntermediateForm,
    visitHuanbeiRegisterPage,
    visitPersonalInfoPage,
    visitCommonContactsPage,
    visitIdCardInfoPage,
    visitIdRetentionPopup,
    clickIdPopup,
    visitDisburseAuthPage,
    clickDisburseAuth,
    trackRouteCodeConvert,
    clickByPeriod,
    trackInvalidLinkPage
  }
}
